import {
  CALLABLE_NAMES,
  type ComplianceRequirements,
  type GetComplianceRequirementsRequest,
  type SubmitComplianceRequest,
  type SubmitComplianceResponse,
  type SyncComplianceStatusRequest,
  type SyncComplianceStatusResponse,
} from '@acs/shared';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, listAll, ref, uploadBytes } from 'firebase/storage';
import { functions, storage } from './firebase.js';

/** Typed callable wrappers (ADR-0003 rule 2). */
const submitComplianceFn = httpsCallable<SubmitComplianceRequest, SubmitComplianceResponse>(
  functions,
  CALLABLE_NAMES.submitCompliance,
);
const syncComplianceStatusFn = httpsCallable<
  SyncComplianceStatusRequest,
  SyncComplianceStatusResponse
>(functions, CALLABLE_NAMES.syncComplianceStatus);
const getRequirementsFn = httpsCallable<GetComplianceRequirementsRequest, ComplianceRequirements>(
  functions,
  CALLABLE_NAMES.getComplianceRequirements,
);

export async function submitCompliance(
  req: SubmitComplianceRequest,
): Promise<SubmitComplianceResponse> {
  return (await submitComplianceFn(req)).data;
}

/**
 * Reconcile the local KYC status with Twilio's live bundle status (poll fallback
 * for a missed `bundleStatusWebhook`). Safe to call on page load; a no-op once the
 * bundle is terminal. The business doc is live-subscribed, so a resulting server
 * write updates the UI on its own.
 */
export async function syncComplianceStatus(
  req: SyncComplianceStatusRequest,
): Promise<SyncComplianceStatusResponse> {
  return (await syncComplianceStatusFn(req)).data;
}

export async function getComplianceRequirements(
  req: GetComplianceRequirementsRequest,
): Promise<ComplianceRequirements> {
  return (await getRequirementsFn(req)).data;
}

/** Storage rules (storage.rules) only accept these content types, < 5 MB. */
const MAX_EVIDENCE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME: Record<string, string> = {
  'application/pdf': 'application/pdf',
  'image/jpeg': 'image/jpeg',
  'image/jpg': 'image/jpeg',
  'image/png': 'image/png',
};
const EXT_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
};

/**
 * Resolve a Storage-rule-valid content type. Browsers frequently report an empty
 * or atypical `File.type` (especially for PDFs), which uploads as
 * `application/octet-stream` and is rejected by the rules' `isAllowedType()` —
 * the real cause of "User does not have permission" on an owned business. Trust
 * the browser type only if it's already allowed; otherwise derive from the
 * filename extension.
 */
function evidenceContentType(file: File): string | null {
  return (
    ALLOWED_MIME[file.type] ?? EXT_MIME[file.name.split('.').pop()?.toLowerCase() ?? ''] ?? null
  );
}

/**
 * Upload one KYC evidence file, tagged by bundle + Twilio document type via the
 * storage path so `submitCompliance` can group them (doc 05 §2.6):
 *   users/{uid}/compliance/{businessId}/{bundleKey}/{twilioType}/{file}
 * The uid segment lets the Storage rules gate on `request.auth.uid == uid` without
 * a cross-service Firestore lookup (storage.rules).
 *
 * Validates type + size up front (mirroring the Storage rules) so an unsupported
 * or oversized file gets a clear message instead of an opaque `storage/unauthorized`.
 */
export async function uploadEvidence(
  uid: string,
  businessId: string,
  bundleKey: string,
  twilioType: string,
  file: File,
): Promise<{ path: string; url: string }> {
  const contentType = evidenceContentType(file);
  if (!contentType) {
    throw new Error(`"${file.name}" must be a PDF, JPEG, or PNG.`);
  }
  if (file.size >= MAX_EVIDENCE_BYTES) {
    throw new Error(`"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is 5 MB.`);
  }
  const path = `users/${uid}/compliance/${businessId}/${bundleKey}/${twilioType}/${Date.now()}-${file.name}`;
  const r = ref(storage, path);
  await uploadBytes(r, file, { contentType });
  return { path, url: await getDownloadURL(r) };
}

/**
 * List already-uploaded evidence for a bundle, grouped by Twilio document type
 * (the sub-folder under the bundle). Lets the onboarding UI reflect what's really
 * in Storage, so a returning owner sees prior uploads and the Submit gate keys off
 * real state instead of just this session's uploads.
 */
export async function listEvidence(
  uid: string,
  businessId: string,
  bundleKey: string,
): Promise<Record<string, string[]>> {
  const r = ref(storage, `users/${uid}/compliance/${businessId}/${bundleKey}`);
  const res = await listAll(r);
  const entries = await Promise.all(
    res.prefixes.map(async (p) => [p.name, (await listAll(p)).items.map((i) => i.name)] as const),
  );
  return Object.fromEntries(entries.filter(([, files]) => files.length > 0));
}
